import React from "react";
import { Box, Button, Typography, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import PlaceIcon from "@mui/icons-material/Place";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface PlotRequestActionsProps {
  selectedCount: number;
  availablePlotCount: number;
  bulkEditEnabled: boolean;
  onAddRow: () => void;
  onBulkEdit: () => void;
  onFilter: () => void;
  onPlotToMap: () => void;
  onDelete: () => void;
  onAcquireBooks?: () => void;
  /** CSV形式ダウンロード実行時のコールバック */
  onDownloadCSV: () => void;
  /** Excel形式ダウンロード実行時のコールバック */
  onDownloadExcel: () => void;
  /** ダウンロードボタンを非活性にするかどうかのフラグ（データ件数0件時など） */
  downloadDisabled: boolean;
}

/**
 * プロットリクエスト詳細のアクションボタン群
 */
export const PlotRequestActions: React.FC<PlotRequestActionsProps> = ({
  selectedCount,
  availablePlotCount,
  bulkEditEnabled,
  onAddRow,
  onBulkEdit,
  onFilter,
  onPlotToMap,
  onDelete,
  onAcquireBooks,
  onDownloadCSV,
  onDownloadExcel,
  downloadDisabled,
}) => {
  // ダウンロードメニューポップオーバーのアンカー要素を管理するState
  const [downloadAnchorEl, setDownloadAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const isDownloadMenuOpen = Boolean(downloadAnchorEl);

  /**
   * ダウンロードボタンクリック時にメニューを展開する
   */
  const handleDownloadClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ): void => {
    setDownloadAnchorEl(event.currentTarget);
  };

  /**
   * ダウンロードメニューを閉じる
   */
  const handleDownloadMenuClose = (): void => {
    setDownloadAnchorEl(null);
  };

  /**
   * CSV形式でのダウンロードを要求し、メニューを閉じる
   */
  const handleDownloadCSVClick = (): void => {
    onDownloadCSV();
    handleDownloadMenuClose();
  };

  /**
   * Excel形式でのダウンロードを要求し、メニューを閉じる
   */
  const handleDownloadExcelClick = (): void => {
    onDownloadExcel();
    handleDownloadMenuClose();
  };
  return (
    <>
      <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          size="small"
          onClick={onAddRow}
        >
          行を追加
        </Button>
        {bulkEditEnabled && (
          <Button
            variant="outlined"
            size="small"
            disabled={selectedCount === 0}
            onClick={onBulkEdit}
          >
            一括で編集
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<FilterAltIcon />}
          size="small"
          onClick={onFilter}
        >
          フィルター
        </Button>
        <Button
          variant="outlined"
          startIcon={<PlaceIcon />}
          size="small"
          onClick={onPlotToMap}
          disabled={availablePlotCount === 0}
        >
          {selectedCount === 0
            ? `地図に反映する (${formatNumberWithComma(availablePlotCount)}件)`
            : `選択した行を地図に反映する (${formatNumberWithComma(
                availablePlotCount
              )}件)`}
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          disabled={selectedCount === 0}
          onClick={onDelete}
        >
          削除
        </Button>
        {onAcquireBooks && (
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            size="small"
            onClick={onAcquireBooks}
            disabled={selectedCount === 0}
          >
            登記取得へ進む
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          endIcon={<KeyboardArrowDownIcon />}
          size="small"
          onClick={handleDownloadClick}
          disabled={downloadDisabled}
        >
          ダウンロード
        </Button>
        <Menu
          anchorEl={downloadAnchorEl}
          open={isDownloadMenuOpen}
          onClose={handleDownloadMenuClose}
        >
          <MenuItem onClick={handleDownloadCSVClick}>
            CSV形式でダウンロード
          </MenuItem>
          <MenuItem onClick={handleDownloadExcelClick}>
            Excel形式でダウンロード
          </MenuItem>
        </Menu>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          ※ 地図への反映は土地のみです
        </Typography>
      </Box>
    </>
  );
};
